export function formatPrice(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(n);
}

/**
 * Παράγει περιγραφικό alt για φωτογραφία προϊόντος, συνδυάζοντας
 * όνομα + brand + κατηγορία (χωρίς διπλές λέξεις). Βοηθά το Google Images
 * να κατανοεί και να εμφανίζει καλύτερα τις εικόνες των προϊόντων.
 */
export function productImageAlt(p: {
  name?: string | null;
  manufacturer_name?: string | null;
  category_name?: string | null;
}): string {
  const name = (p.name ?? "").trim();
  const lower = name.toLowerCase();
  const parts: string[] = [];
  if (name) parts.push(name);
  const brand = p.manufacturer_name?.trim();
  if (brand && !lower.includes(brand.toLowerCase())) parts.push(brand);
  const category = p.category_name?.trim();
  if (category && !lower.includes(category.toLowerCase())) parts.push(category);
  return parts.join(" – ") || "Προϊόν MrPanda.gr";
}

/**
 * Επιστρέφει το κείμενο διαθεσιμότητας ενός προϊόντος. Αν δεν έχει οριστεί
 * τιμή `availability` στη βάση, παράγει fallback με βάση το απόθεμα ώστε να
 * εμφανίζεται πάντα ένδειξη διαθεσιμότητας.
 */
export function resolveAvailability(
  availability: string | null | undefined,
  stockQuantity: number | null | undefined,
): string {
  const trimmed = (availability ?? "").trim();
  if (trimmed) return trimmed;
  const stock = Number(stockQuantity ?? 0);
  return stock > 0 ? "Διαθέσιμο" : "Κατόπιν παραγγελίας";
}

/** CSS class χρώματος ανάλογα με το κείμενο διαθεσιμότητας. */
export function availabilityColor(availability: string | null | undefined): string {
  const v = (availability ?? "").toLowerCase();
  if (v.includes("άμεσα")) return "text-green-700";
  if (v.includes("διαθέσιμο")) return "text-blue-700";
  if (v.includes("παραγγελ")) return "text-amber-700";
  return "text-muted-foreground";
}

/** Tailwind classes για badge-style ένδειξη διαθεσιμότητας. */
export function availabilityBadgeClass(availability: string | null | undefined): string {
  const v = (availability ?? "").toLowerCase();
  if (v.includes("άμεσα")) return "bg-green-600 text-white";
  if (v.includes("διαθέσιμο")) return "bg-blue-600 text-white";
  if (v.includes("παραγγελ")) return "bg-amber-500 text-white";
  return "bg-gray-500 text-white";
}

const GREEKLISH_MAP: Record<string, string> = {
  α: "a", ά: "a", β: "v", γ: "g", δ: "d", ε: "e", έ: "e", ζ: "z",
  η: "i", ή: "i", θ: "th", ι: "i", ί: "i", ϊ: "i", ΐ: "i",
  κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", ό: "o",
  π: "p", ρ: "r", σ: "s", ς: "s", τ: "t", υ: "y", ύ: "y", ϋ: "y", ΰ: "y",
  φ: "f", χ: "ch", ψ: "ps", ω: "o", ώ: "o",
};

export function greeklish(input: string): string {
  const lower = (input ?? "").toLowerCase();
  let out = "";
  for (const ch of lower) out += GREEKLISH_MAP[ch] ?? ch;
  return out
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function slugify(input: string): string {
  return input
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function productSlug(
  name: string,
  psId: number | string | null | undefined,
  fallbackUuid?: string,
): string {
  const name2 = greeklish(name) || "p";
  if (psId != null && String(psId).length > 0) return `${psId}-${name2}`;
  // Legacy fallback (uuid) όταν δεν έχουμε ps_id
  return `${name2}--${fallbackUuid ?? ""}`;
}

/**
 * Επιστρέφει το αναγνωριστικό από ένα product slug.
 * - Νέα μορφή `{ps_id}-{greeklish}` → επιστρέφει τον αριθμό ως string.
 * - Παλιά μορφή `{name}--{uuid}` → επιστρέφει το uuid prefix.
 */
export function idFromSlug(slug: string): string {
  const m = /^(\d+)(?:-|$)/.exec(slug);
  if (m) return m[1];
  const idx = slug.lastIndexOf("--");
  if (idx >= 0) return slug.slice(idx + 2);
  // Παλιά PrestaShop μορφή `{slug}-{id}` (id στο τέλος), π.χ. `mega-fire-2766`.
  const t = /-(\d+)$/.exec(slug);
  if (t) return t[1];
  return slug;
}
